import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapPin, ArrowLeft, Search, Store as StoreIcon, AlertCircle } from 'lucide-react';
import { stores as storesApi } from '../lib/api';
import StoreCard from '../components/StoreCard';

export default function PincodeStores() {
  const { pin } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [category, setCategory] = useState('');

  useEffect(() => {
    let active = true;
    storesApi.byPincode(pin)
      .then(d => { if (active) { setData(d); setLoading(false); } })
      .catch(e => { if (active) { setErr(e?.response?.data?.detail || 'Could not load shops for this PIN'); setLoading(false); } });
    return () => { active = false; };
  }, [pin]);

  const stores = (data?.stores || []).filter(s => !category || s.category === category);
  const categories = Array.from(new Set((data?.stores || []).map(s => s.category))).sort();

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
      <button data-testid="pincode-back" onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm text-ink/60 hover:text-ink mb-4">
        <ArrowLeft className="w-4 h-4"/> Back
      </button>

      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-14 h-14 rounded-xl bg-[#0D47A1] text-white grid place-items-center">
          <MapPin className="w-7 h-7"/>
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0D47A1]">PIN · {pin}</div>
          <h1 className="font-heading font-black text-3xl md:text-4xl mt-1" data-testid="pincode-title">Shops serving {pin}</h1>
          {data && (
            <p className="text-sm text-ink/70 mt-1">
              {data.total_stores} shop{data.total_stores === 1 ? '' : 's'} across{' '}
              {data.villages.length} village{data.villages.length === 1 ? '' : 's'}
              {data.villages.length > 0 && (
                <> · <span className="font-semibold">{data.villages.map(v => v.village).join(', ')}</span></>
              )}
            </p>
          )}
        </div>
        <Link to="/stores" data-testid="pincode-browse-all" className="btn-secondary !py-2 inline-flex items-center gap-1.5">
          <Search className="w-4 h-4"/> Browse all
        </Link>
      </div>

      {loading && <div className="py-16 text-center text-ink/50">Loading shops in {pin}…</div>}

      {err && (
        <div data-testid="pincode-error" className="mt-8 card-soft p-6 flex items-start gap-3 border-[#FBE9E7]">
          <AlertCircle className="w-5 h-5 text-[#BF360C] shrink-0 mt-0.5"/>
          <div>
            <div className="font-heading font-bold text-lg">We couldn&apos;t load shops for {pin}</div>
            <p className="text-sm text-ink/70 mt-1">{err}</p>
            <Link to="/" className="text-sm font-semibold text-[#D35400] inline-flex items-center gap-1 mt-2">
              Go back home
            </Link>
          </div>
        </div>
      )}

      {!loading && !err && data && data.total_stores === 0 && (
        <div data-testid="pincode-empty" className="mt-8 card-soft p-8 text-center">
          <StoreIcon className="w-10 h-10 text-ink/30 mx-auto mb-3"/>
          <div className="font-heading font-bold text-xl">No shops yet in {pin}</div>
          <p className="text-sm text-ink/60 mt-1">Be the first to invite a local kirana, vegetable or fancy store to BharatCart.</p>
          <Link to="/become-seller" className="btn-primary !py-2 inline-flex items-center gap-1.5 mt-4">
            <StoreIcon className="w-4 h-4"/> Invite a store
          </Link>
        </div>
      )}

      {!loading && !err && data && data.total_stores > 0 && (
        <>
          {categories.length > 1 && (
            <div className="mt-6 flex gap-2 flex-wrap" data-testid="pincode-category-chips">
              <button
                data-testid="pincode-cat-all"
                onClick={() => setCategory('')}
                className={`pill ${category === '' ? '!bg-[#0D47A1] !text-white !border-[#0D47A1]' : ''}`}
              >
                All ({data.stores.length})
              </button>
              {categories.map(c => {
                const count = data.stores.filter(s => s.category === c).length;
                return (
                  <button
                    key={c}
                    data-testid={`pincode-cat-${c}`}
                    onClick={() => setCategory(c)}
                    className={`pill ${category === c ? '!bg-[#0D47A1] !text-white !border-[#0D47A1]' : ''}`}
                  >
                    {c} ({count})
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pincode-store-grid">
            {stores.map(s => <StoreCard key={s.id} store={s}/>)}
          </div>

          {stores.length === 0 && (
            <div className="py-10 text-center text-ink/50" data-testid="pincode-filter-empty">
              No shops in this category for {pin}. Pick another tag above.
            </div>
          )}
        </>
      )}
    </div>
  );
}
